/**
 * Schema-management scheduling / draft CRUD:
 * - list retrieval (`fetchScheduledDraftHistoryList`)
 * - draft config loader (`loadScheduledDraftConfig`, `loadHistoryConfig`)
 * - request orchestrator (`handleScheduleDraftRequest`)
 * - save-with-defaults (`saveScheduleWithSchemaDefaults`)
 * - delete (`deleteScheduledDraft`)
 *
 * Also colocates two internal helpers used only here:
 * `checkSchedulingDraftRequirements` (multischeduler-conflict check) and
 * `displaySummaryModal` (modal dispatch trio).
 *
 * `saveScheduleWithSchemaDefaults` adopts `runAsyncAction`.
 */

import { Fragment } from "react";
import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import find from "lodash/find";
import { diff } from "deep-object-diff";
import { getStore } from "@store";
import schemaManagementAdapter from "@features/schema-management/adapters";
import { isEmptyObject, stringifyNestedKeyPaths } from "@utils/object-helpers";
import { getDateDifference } from "@utils/time-helpers";
import {
  generateBlankConfig,
} from "@features/sypi/_utils/sypi-interface-helpers";
import {
  currentKeysOccurrenceChangesReport,
  logDiffs,
  scheduleDraftReport,
  mergeSmVersionChanges,
} from "@features/schema-management/utils/schemaManagementHelpers";
import { CONFIG_SCHEDULER } from "@actions/types";
import { STATE_CONTANTS } from "@constants";
import {
  DISPATCH_ERRORS,
  SCHEDULE_DRAFT_STATUS,
  SCHEDULE_DRAFT_ACTIONS,
  SCHEDULE_DRAFT_SAVE_STATE,
  SYF_CARD_ACCESS,
  SYPI_BRC,
} from "@constants/schema-management";
import { runAsyncAction } from "@utils/runAsyncAction";
import {
  showSpinner,
  stopSpinner,
  reportError,
  closeDock,
  clearChanges,
  clearLog,
  enableSave,
  clickTab,
  setAlertDock,
  recordChangeObject,
  logChangedKey,
} from "@actions/global";
import {
  loadHistorySet,
  loadScheduledDraftSet,
  loadSummaryChangesReview,
  loadMasterSchema,
  resetSMSchedulerSetUp,
  setScheduledHistoryView,
  setSummaryViewType,
  setSmConfigStatus,
  toggleSummaryModal,
  toggleSummaryConfirm,
  enableSummaryPostAction,
  setDeletedClients,
  isScheduleChanges,
} from "@features/schema-management/actions";
import { mergeConfigChanges } from "./sm-drafts";
import { fetchSchema } from "./sm-fetch";
import { checkForDuplicateClient } from "./sm-import";
import {
  handleSummaryPreviewAndRequirements,
  handleUpdateConfigRequest,
  cleanUpScheduleDraftChanges,
  sendFinalReport,
} from "./sm-summary";
import { updateToSchemaDefaultsSummaryPreview } from "./sm-defaults";

/* Config Scheduler Functions */
export const fetchScheduledDraftHistoryList = async (
  activeReleaseVersion,
  fetchScheduledDraft = true,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const { featureName } = store.getState().schemaAllView;
  const isCommercial = featureName === SYPI_BRC;
  let scheduleDraftList = fetchScheduledDraft
    ? await schemaManagementAdapter.fetchScheduledDraftList({}, isCommercial)
    : await schemaManagementAdapter.fetchHistoryConfig({});
  let { apiError, configSchedules } = scheduleDraftList;
  if (apiError && apiError.details) {
    store.dispatch(
      reportError("Unable to load the scheduled and drafts configurations")
    );
  } else {
    if (fetchScheduledDraft) {
      store.dispatch(loadScheduledDraftSet(configSchedules));
    } else {
      let historySet = configSchedules.map(
        (configSchedule) =>
          (configSchedule = {
            ...configSchedule,
            revertable: !(
              getDateDifference(configSchedule.liveScheduleDate) > 30
            ),
          })
      );
      store.dispatch(loadHistorySet(historySet));
    }
  }
  store.dispatch(stopSpinner());
};

export const loadScheduledDraftConfig = async (
  schemaPayload = null,
  importedConfig = null,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  const summaryView = schemaPayload?.summaryView;
  const { changeDict, initialConfig, importedChanges } =
    store.getState().schemaAllView;
  let inputSourceKeysReport = {};
  if (
    changeDict &&
    !isEmptyObject(changeDict) &&
    importedChanges &&
    !isEmptyObject(importedChanges)
  ) {
    Object.keys(importedChanges).forEach((key) => {
      inputSourceKeysReport[key] = {
        imported:
          changeDict[key] === importedChanges[key] ||
          (!changeDict[key] && !importedChanges[key]),
      };
    });
  }
  let summaryReviewBundle =
    summaryView && summaryView.length
      ? [...summaryView]
      : [
          ...(changeDict && !isEmptyObject(changeDict)
            ? mergeConfigChanges(
                changeDict,
                initialConfig,
                inputSourceKeysReport
              )
            : []),
        ];

  store.dispatch(
    loadSummaryChangesReview(summaryReviewBundle, importedConfig || null)
  );
  store.dispatch(stopSpinner());
};

export const loadHistoryConfig = async (historyConfig, store = getStore()) => {
  store.dispatch(showSpinner());
  const storeContents = store.getState();
  const { historySet } = storeContents.schemaConfigScheduler;
  const scheduledHistoryConfig = find(
    historySet,
    (historyCF) =>
      historyCF.liveScheduleDate === historyConfig.liveScheduleDate &&
      historyCF.release_version === historyConfig.release_version
  );
  if (scheduledHistoryConfig && !isEmptyObject(scheduledHistoryConfig)) {
    store.dispatch(setScheduledHistoryView(scheduledHistoryConfig));
  }
  store.dispatch(stopSpinner());
};

// Internal: verifies the user's pending change doesn't collide with an
// already-scheduled multischeduler config change. Returns the report to
// display and the common-key report used by the summary preview.
const checkSchedulingDraftRequirements = async (
  summaryViewType,
  status,
  store = getStore()
) => {
  let requirementsReport = {
      type: "TOAST_MESSAGE",
      message: "Summary of changes is ready to review",
    },
    hasRequirements = true;

  const storeValues = store.getState();
  const {
    schema: { activeReleaseVersion },
    schemaAllView: { changeDict, feature },
    schemaConfigScheduler: {
      initialScheduleDraft,
      scheduleDraftSet,
      schemaConfigSaveState,
    },
  } = storeValues;

  store.dispatch(setSummaryViewType(summaryViewType));
  const isCommercial = feature === SYPI_BRC;
  const { configSchedules } =
    scheduleDraftSet && scheduleDraftSet.length
      ? { configSchedules: scheduleDraftSet }
      : await schemaManagementAdapter.fetchScheduledDraftList({}, isCommercial);
  let filteredScheduledChanges =
    schemaConfigSaveState === SCHEDULE_DRAFT_SAVE_STATE.EDIT
      ? configSchedules?.filter(
          (configSchedule) =>
            configSchedule.status === "scheduled" &&
            configSchedule.release_version === activeReleaseVersion &&
            configSchedule.liveScheduleDate !==
              initialScheduleDraft.liveScheduleDate
        )
      : configSchedules?.filter(
          (configSchedule) =>
            configSchedule.status === "scheduled" &&
            configSchedule.release_version === activeReleaseVersion
        );
  let { scheduledChangesWithCommonKeys, successFailedKeysReport } =
    status !== STATE_CONTANTS.DRAFT_STATUS
      ? currentKeysOccurrenceChangesReport(changeDict, filteredScheduledChanges)
      : {};
  if (
    scheduledChangesWithCommonKeys &&
    !isEmptyObject(scheduledChangesWithCommonKeys)
  ) {
    hasRequirements = false;
    store.dispatch(
      clickTab(STATE_CONTANTS.GENERAL_SCHEDULER_ERROR, CONFIG_SCHEDULER)
    );
    store.dispatch(toggleSummaryConfirm(true));
    requirementsReport = {
      type: "ALERT_DOCK",
      title: "Multischeduler Error",
      message: (
        <Fragment>
          The following keys already have 3 or more changes scheduled in
          configuration file {activeReleaseVersion}:
          <br />
          {Object.keys(scheduledChangesWithCommonKeys).map((key) => (
            <b key={key}>
              {`${key}.`} <br />
            </b>
          ))}
        </Fragment>
      ),
    };
  }
  return {
    hasRequirements,
    requirementsReport,
    scheduledChangesWithCommonKeys,
    successFailedKeysReport,
  };
};

// Internal: single-line abstraction for the modal-open-and-configure trio.
const displaySummaryModal = (
  displayedState,
  summaryType,
  configStatus,
  enableSummaryPost = false,
  store = getStore()
) => {
  store.dispatch(enableSummaryPostAction(enableSummaryPost));
  store.dispatch(toggleSummaryModal(displayedState));
  store.dispatch(setSummaryViewType(summaryType));
  store.dispatch(setSmConfigStatus(configStatus));
};

export const handleScheduleDraftRequest = async (
  action,
  typeOfChange = SCHEDULE_DRAFT_STATUS.SCHEDULED,
  scheduleNewDefaults = false,
  store = getStore()
) => {
  store.dispatch(showSpinner());
  let finalReport = {};
  const { schemaAllView } = store.getState();
  const { featureName } = schemaAllView;
  if (action === SCHEDULE_DRAFT_ACTIONS.START_SCHEDULE_DRAFT_PROCESS) {
    if (typeOfChange === SCHEDULE_DRAFT_STATUS.SCHEDULED)
      store.dispatch(isScheduleChanges(true));
    const {
      requirementsReport,
      scheduledChangesWithCommonKeys,
      successFailedKeysReport,
    } = await checkSchedulingDraftRequirements(
      STATE_CONTANTS.SUMMARY,
      typeOfChange
    );
    if (
      !scheduledChangesWithCommonKeys ||
      isEmptyObject(scheduledChangesWithCommonKeys)
    ) {
      store.dispatch(
        clickTab(STATE_CONTANTS.SCHEDULER_STATUS, CONFIG_SCHEDULER)
      );
    }
    if (scheduleNewDefaults) {
      await updateToSchemaDefaultsSummaryPreview(scheduleNewDefaults);
    }
    finalReport = requirementsReport;
    await handleSummaryPreviewAndRequirements(
      scheduledChangesWithCommonKeys,
      successFailedKeysReport
    );
    displaySummaryModal(true, STATE_CONTANTS.SUMMARY, typeOfChange, true);
  } else if (action === SCHEDULE_DRAFT_ACTIONS.SUBMIT_SUMMARY) {
    displaySummaryModal(true, STATE_CONTANTS.FORM, typeOfChange);
  } else if (action === SCHEDULE_DRAFT_ACTIONS.SUBMIT_FORM) {
    const scheduledDraftResults = await handleUpdateConfigRequest(
      typeOfChange,
      scheduleNewDefaults
    );
    await cleanUpScheduleDraftChanges(scheduledDraftResults);
    finalReport = scheduleDraftReport(scheduledDraftResults, featureName);
  }
  if (!isEmptyObject(finalReport)) {
    sendFinalReport(finalReport);
  }

  store.dispatch(stopSpinner());
};

/**
 * Merges the master-schema defaults into the client config (if the schema
 * changed) and records the resulting diff for scheduling. Uses
 * `runAsyncAction` for spinner + centralized error surface.
 */
export const saveScheduleWithSchemaDefaults = async (store = getStore()) => {
  return await runAsyncAction(
    store,
    async () => {
      const { initialConfig, featureName } = store.getState().schemaAllView;
      const { activeReleaseVersion } = store.getState().schema;
      const { formattedSchema, formattedMasterSchema, rawMasterSchema } =
        await fetchSchema(activeReleaseVersion);
      const schemasDifference = diff(formattedSchema, formattedMasterSchema);

      const config = store.getState().schemaAllView.schemaManagementConfig;
      const clientConfigTemplate = generateBlankConfig(formattedSchema);
      const {
        fullErrorReport: { schemaErrors },
      } = DISPATCH_ERRORS[featureName](config, formattedMasterSchema, "", false);

      if (
        (schemasDifference && !isEmptyObject(schemasDifference)) ||
        (schemaErrors && schemaErrors?.length)
      ) {
        let masterConfigTemplate = generateBlankConfig(formattedMasterSchema);

        const savedConfigChanges = diff(clientConfigTemplate, config);

        const pathConfigChanges =
          savedConfigChanges && !isEmptyObject(savedConfigChanges)
            ? stringifyNestedKeyPaths(savedConfigChanges)
            : [];

        let mergeUpdatedConfig =
          pathConfigChanges && pathConfigChanges.length
            ? pathConfigChanges.reduce((accSmConfig, path) => {
                if (has(accSmConfig, path) && has(config, path)) {
                  const savedValue = get(config, path);
                  set(accSmConfig, path, savedValue);
                }
                return accSmConfig;
              }, masterConfigTemplate)
            : masterConfigTemplate;

        let loggedDiffs = logDiffs(initialConfig, mergeUpdatedConfig);
        store.dispatch(recordChangeObject(loggedDiffs));
        store.dispatch(logChangedKey(Object.keys(loggedDiffs)));
        store.dispatch(
          loadMasterSchema(formattedMasterSchema, rawMasterSchema)
        );
        DISPATCH_ERRORS[featureName](mergeUpdatedConfig, formattedMasterSchema);
      }
    },
    {
      errorAction: "save",
      errorSubject: "schedule with schema defaults",
    }
  );
};

export const deleteScheduledDraft = async (
  scheduleDraft,
  deleteStatus,
  store = getStore()
) => {
  store.dispatch(showSpinner());

  const storeContents = store.getState();
  const isScheduled = deleteStatus === SCHEDULE_DRAFT_ACTIONS.DELETE_SCHEDULED;
  let finalReport = null;
  const deleteConfig = isScheduled
    ? {
        live_schedule_date: scheduleDraft.liveScheduleDate,
        release_version: scheduleDraft.release_version,
      }
    : {
        name: scheduleDraft.name,
        release_version: scheduleDraft.release_version,
      };
  let { schemaConfigScheduler, schemaAllView } = storeContents;
  let { featureName, isAthosConfig } = schemaAllView;
  const isCommercial = featureName === SYPI_BRC;
  let result = isScheduled
    ? await schemaManagementAdapter.deleteScheduledConfig(
        deleteConfig,
        isCommercial
      )
    : await schemaManagementAdapter.deleteDraftConfig(
        deleteConfig,
        isCommercial
      );

  let { success } = result;
  if (!success) {
    finalReport = {
      type: "ALERT_DOCK",
      title: `Deleting the ${scheduleDraft.name} was unsuccessful. `,
      message:
        "Please contact the application development team for assistance.",
    };
  } else if (success) {
    let { scheduleDraftSet } = schemaConfigScheduler;
    const updatedScheduleDraftSet = scheduleDraftSet.filter(
      (config) =>
        !(isScheduled
          ? config.liveScheduleDate === deleteConfig.live_schedule_date &&
            config.release_version === deleteConfig.release_version
          : config.status === "draft" && config.name === deleteConfig.name)
    );
    store.dispatch(loadScheduledDraftSet(updatedScheduleDraftSet));
    store.dispatch(resetSMSchedulerSetUp());
    finalReport = {
      type: "TOAST_MESSAGE",
      message: `Deleting ${scheduleDraft.name} was successful`,
    };
  }
  if (featureName === SYF_CARD_ACCESS || isAthosConfig) {
    checkForDuplicateClient(null, featureName);
  }
  store.dispatch(closeDock());
  store.dispatch(enableSave());
  store.dispatch(stopSpinner());
  sendFinalReport(finalReport);
};
